/**
 * Clean HTML pasted from Word, Outlook, Gmail, web pages, etc. so it
 * doesn't bring foreign spacing/margins/classes into our editor.
 *
 * Activated only on the editor's paste event. Doesn't run on typed input,
 * toolbar formatting, drafts, templates, or programmatic innerHTML writes.
 */

const STYLE_PROPS_TO_STRIP = new Set([
  'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
  'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
  'line-height', 'text-indent',
  'page-break-before', 'page-break-after', 'page-break-inside',
]);

function stripClasses(el) {
  if (!el.className || typeof el.className !== 'string') return;
  const cls = el.className.split(/\s+/).filter((c) => !/^(Mso|mso)/i.test(c));
  if (cls.length === 0) el.removeAttribute('class');
  else el.className = cls.join(' ');
}

function stripStyles(el) {
  const styleAttr = el.getAttribute('style');
  if (!styleAttr) return;
  const kept = styleAttr
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((decl) => {
      const prop = decl.split(':')[0]?.trim().toLowerCase();
      if (!prop) return false;
      if (/^mso-/i.test(prop)) return false;
      return !STYLE_PROPS_TO_STRIP.has(prop);
    });
  if (kept.length === 0) el.removeAttribute('style');
  else el.setAttribute('style', kept.join('; '));
}

function convertParagraphToDiv(el) {
  const div = document.createElement('div');
  Array.from(el.attributes).forEach((attr) => {
    div.setAttribute(attr.name, attr.value);
  });
  while (el.firstChild) div.appendChild(el.firstChild);
  // Empty paragraph (Word's "&nbsp;" blank line) — preserve line-break intent
  if (!div.textContent.replace(/ /g, '').trim() && !div.querySelector('img')) {
    div.innerHTML = '<br>';
  }
  el.parentNode?.replaceChild(div, el);
}

export function cleanPastedHtml(rawHtml) {
  if (!rawHtml) return '';
  if (typeof window === 'undefined' || !window.document) return rawHtml;

  // Strip top-level Office cruft before parsing
  let html = rawHtml
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(
      /<(meta|link|style|xml|title|head|html|body)\b[^>]*>[\s\S]*?<\/\1>/gi,
      ''
    )
    .replace(/<(meta|link|style|xml|title|head|html|body)\b[^>]*\/?>/gi, '')
    .replace(/<\/?(o|w|m|v|x|st1):[a-z][^>]*>/gi, '');

  const wrapper = window.document.createElement('div');
  wrapper.innerHTML = html;

  // Snapshot all elements first (querySelectorAll is static) so we can
  // safely mutate during iteration.
  const elements = Array.from(wrapper.querySelectorAll('*'));
  elements.forEach((el) => {
    stripClasses(el);
    stripStyles(el);
  });

  // Convert <p> → <div> so we don't inherit Word's or email clients'
  // default <p>-margin behavior. Matches what Gmail does internally.
  Array.from(wrapper.querySelectorAll('p')).forEach(convertParagraphToDiv);

  return wrapper.innerHTML;
}

export default cleanPastedHtml;
